import { useLayoutEffect, useState } from 'react';
import { breakpoints } from 'src/common/services/mediaQuery.service';
import { giveawayBanner, header } from 'src/theme';

/** Must match `id` on the fixed header root in `header.layout.tsx` */
export const FIXED_HEADER_DOM_ID = 'app-fixed-header';

export function measureFixedHeaderBottomPx(): number {
  const headerEl = document.getElementById(FIXED_HEADER_DOM_ID);
  if (!headerEl) return 0;
  // Parent box must not use a fixed height smaller than children (see header.layout).
  // Max with each child so we never under-count if a child paints outside the parent's layout box.
  let { bottom } = headerEl.getBoundingClientRect();
  Array.from(headerEl.children).forEach((child) => {
    const { bottom: childBottom } = child.getBoundingClientRect();
    bottom = Math.max(bottom, childBottom);
  });
  return bottom;
}

function fallbackPaddingTopPx(): number {
  if (typeof window !== 'undefined' && window.matchMedia(`(min-width: ${breakpoints.laptopL}px)`).matches) {
    return giveawayBanner.height + header.height + 8;
  }
  return giveawayBanner.mobileHeight + header.mobileHeight + 8;
}

/** Viewport distance from top to bottom of the fixed header (updates when header resizes, e.g. HeaderData). */
export function useFixedHeaderOffsetPx(): number {
  const [px, setPx] = useState(fallbackPaddingTopPx);

  useLayoutEffect(() => {
    const update = () => {
      const m = measureFixedHeaderBottomPx();
      setPx((prev) => (m > 0 ? m : prev));
    };

    const headerEl = document.getElementById(FIXED_HEADER_DOM_ID);
    if (!headerEl) {
      update();
      return undefined;
    }

    const ro = new ResizeObserver(update);
    ro.observe(headerEl);

    const observedChildren = new WeakSet<Element>();
    const observeChild = (child: Element) => {
      if (observedChildren.has(child)) return;
      observedChildren.add(child);
      ro.observe(child);
    };
    Array.from(headerEl.children).forEach((child) => observeChild(child));

    const mo = new MutationObserver(() => {
      Array.from(headerEl.children).forEach((child) => observeChild(child));
      update();
    });
    mo.observe(headerEl, { childList: true, subtree: false });

    update();

    window.addEventListener('resize', update);
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }

    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect();
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', onScroll);
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
    };
  }, []);

  return px;
}
